import { Link } from "react-router-dom";
import { Scissors, Tag, Images, Clock, Building2, Settings, Home, Palette, ExternalLink, Pencil, CalendarCheck } from "lucide-react";
import { usePageData, PageTitle, Card, Spinner } from "../components/admin-ui";

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-stone-200/80 shadow-sm p-5" data-testid={testid}>
      <div className="inline-flex p-2 rounded-lg bg-[#4A1D24]/5 text-[#4A1D24]"><Icon className="h-4 w-4" /></div>
      <p className="text-3xl font-semibold text-stone-900 mt-3">{value}</p>
      <p className="text-xs uppercase tracking-widest text-stone-400 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
    </div>
  );
}

const SHORTCUTS = [
  { to: "/website-admin/startseite", icon: Home, label: "Startseite bearbeiten" },
  { to: "/website-admin/terminanfragen", icon: CalendarCheck, label: "Terminanfragen prüfen" },
  { to: "/website-admin/branding", icon: Palette, label: "Logo & Branding anpassen" },
  { to: "/website-admin/dienstleistungen", icon: Scissors, label: "Dienstleistung hinzufügen" },
  { to: "/website-admin/angebote", icon: Tag, label: "Angebot erstellen" },
  { to: "/website-admin/galerie", icon: Images, label: "Bilder hochladen" },
  { to: "/website-admin/oeffnungszeiten", icon: Clock, label: "Öffnungszeiten ändern" },
  { to: "/website-admin/geschaeft", icon: Building2, label: "Kontaktdaten pflegen" },
  { to: "/website-admin/einstellungen", icon: Settings, label: "SEO & Einstellungen" },
];

export default function Overview() {
  const { data, loading } = usePageData("/website-admin/overview");
  if (loading || !data) return <Spinner />;
  return (
    <div data-testid="overview-page">
      <PageTitle title="Übersicht" text="Status Ihrer Website auf einen Blick."
        action={<a href="/" target="_blank" rel="noreferrer" data-testid="overview-preview-button" className="inline-flex items-center gap-2 text-sm font-semibold text-[#4A1D24] px-4 py-2 rounded-full border border-[#4A1D24]/30 hover:bg-[#4A1D24]/5"><ExternalLink className="h-4 w-4" /> Website ansehen</a>} />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={Scissors} label="Dienstleistungen" value={data.total_services} sub={`${data.active_services} aktiv`} testid="stat-services" />
        <Stat icon={Tag} label="Aktive Angebote" value={data.active_promotions} sub={`${data.total_promotions} gesamt`} testid="stat-promotions" />
        <Stat icon={Images} label="Galeriebilder" value={data.gallery_images} testid="stat-gallery" />
        <Stat icon={CalendarCheck} label="Offene Terminanfragen" value={data.pending_appointments ?? 0} testid="stat-appointments" />
        <Stat icon={Settings} label="Website" value={data.published ? "Online" : "Offline"} sub={data.published ? "öffentlich sichtbar" : "Wartungsmodus"} testid="stat-published" />
      </div>
      <div className="grid lg:grid-cols-12 gap-6 mt-8">
        <Card title="Schnellzugriff" className="lg:col-span-5" testid="overview-shortcuts">
          <div className="grid gap-2">
            {SHORTCUTS.map((s) => (
              <Link key={s.to} to={s.to} className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-[#FAF7F2] text-sm text-stone-700 group">
                <s.icon className="h-4 w-4 text-[#C59B6C]" /><span className="flex-1">{s.label}</span><Pencil className="h-3.5 w-3.5 text-stone-300 group-hover:text-[#4A1D24]" />
              </Link>
            ))}
          </div>
        </Card>
        <Card title="Zuletzt geändert" className="lg:col-span-7" testid="overview-recent">
          {data.recent.length === 0 && <p className="text-sm text-stone-500">Noch keine Änderungen.</p>}
          <ul className="divide-y divide-stone-100">
            {data.recent.map((r, i) => (
              <li key={i} className="flex items-center gap-3 py-2.5 text-sm">
                <span className="text-[10px] uppercase tracking-widest text-[#C59B6C] w-28 shrink-0">{r.type}</span>
                <Link to={`/website-admin/${r.section}`} className="flex-1 text-stone-800 truncate hover:underline">{r.name}</Link>
                <span className="text-xs text-stone-400 whitespace-nowrap">{new Date(r.updated_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
